import React from "react";
import { motion } from "framer-motion";
import { useQuery } from "@tanstack/react-query";
import { ChevronRight, Users } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

interface GameCardProps {
  coverUrl: string | null;
  name: string;
  gameId?: string;
  onClick?: () => void;
}

export const GameCard: React.FC<GameCardProps> = ({ coverUrl, name, gameId, onClick }) => {
  const [imageError, setImageError] = React.useState(false);
  
  // Buscar número de mentores para este jogo
  const { data: mentorCount = 0 } = useQuery({
    queryKey: ["game-mentors", gameId],
    queryFn: async (): Promise<number> => {
      if (!gameId) return 0;
      
      // Buscar mentores únicos que têm offerings ativas para este jogo
      const { data, error } = await supabase
        .from("mentorship_offerings")
        .select("mentor_id")
        .eq("game_id", gameId)
        .eq("is_active", true);
      
      if (error) {
        console.error("Error fetching mentor count for game:", name, error);
        return 0;
      }
      
      // Contar mentores únicos
      const uniqueMentors = new Set(data?.map(offering => offering.mentor_id) || []);
      return uniqueMentors.size;
    },
    enabled: !!gameId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Add logging to help debug image loading issues
  React.useEffect(() => {
    if (coverUrl) {
      console.log(`GameCard: Rendering ${name} with image URL: ${coverUrl}`);
    } else {
      console.log(`GameCard: No image URL for ${name}`);
    }
  }, [coverUrl, name]);

  // Function to handle fallback image
  const getFallbackImageUrl = () => {
    const encodedName = encodeURIComponent(name);
    return `https://placehold.co/500x280/252B36/00B6F0?text=${encodedName}`;
  };

  return (
    <motion.div
      className="group relative w-80 h-72 bg-gradient-to-br from-[#1A1F2C] to-[#0F1419] rounded-2xl overflow-hidden cursor-pointer shadow-xl hover:shadow-2xl border border-[#223342] hover:border-[#00B6F0]/50 transition-all duration-300"
      onClick={onClick}
      whileHover={{ 
        scale: 1.05,
        y: -8,
        transition: { duration: 0.3, ease: "easeOut" }
      }}
      whileTap={{ scale: 0.98 }}
    >
      {/* Background Image */}
      <div className="absolute inset-0">
        {coverUrl && !imageError ? (
          <img 
            src={coverUrl} 
            alt={`Capa de ${name}`} 
            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
            onError={(e) => {
              console.error(`Error loading game image for ${name}:`, e);
              console.error(`Failed URL: ${coverUrl}`);
              setImageError(true);
            }}
            onLoad={() => console.log(`Image for ${name} loaded successfully`)}
          />
        ) : (
          <div 
            className="w-full h-full bg-gradient-to-br from-[#223342] to-[#1A1F2C] flex items-center justify-center"
            style={{
              backgroundImage: `url("${getFallbackImageUrl()}")`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
            }}
          />
        )}
      </div>

      {/* Gradient Overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent group-hover:from-black/90 transition-all duration-300" />
      
      {/* Glow Effect */}
      <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
        <div className="absolute inset-0 bg-gradient-to-t from-[#00B6F0]/20 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-[#00B6F0]/30 to-transparent" />
      </div>

      {/* Content */}
      <div className="absolute bottom-0 left-0 right-0 p-6 z-10">
        <div className="flex items-end justify-between">
          <div className="flex-1">
            <h3 className="text-xl font-bold text-white mb-2 group-hover:text-[#51EFFF] transition-colors">
              {name}
            </h3>
            
            {/* Mentor Count - só mostra se tiver gameId */}
            {gameId && (
              <div className="flex items-center gap-2 text-sm text-gray-300 group-hover:text-[#00B6F0] transition-colors">
                <Users className="h-4 w-4" />
                <span className="font-medium">
                  {mentorCount} {mentorCount === 1 ? 'mentor' : 'mentores'}
                </span>
              </div>
            )}
          </div>

          {/* Play Button */}
          <div className="flex-shrink-0 ml-4">
            <div className="w-12 h-12 bg-[#00B6F0] rounded-full flex items-center justify-center group-hover:bg-[#51EFFF] group-hover:shadow-lg group-hover:shadow-[#00B6F0]/50 transition-all duration-300">
              <ChevronRight className="h-6 w-6 text-black ml-0.5" />
            </div>
          </div>
        </div>
      </div>

      {/* Top Badge - Popular para jogos com 5+ mentores */}
      {gameId && mentorCount > 5 && (
        <div className="absolute top-4 left-4 bg-gradient-to-r from-[#00B6F0] to-[#51EFFF] text-black text-xs font-bold px-3 py-1 rounded-full z-10">
          Popular
        </div>
      )}
    </motion.div>
  );
};
